import { useEffect, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { Card, Section, TH, TD, Empty, Badge, Button } from "../components/ui";
import { StatusBadge } from "./Dashboard";
import { ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => (d ? new Date(d).toLocaleString("it-IT") : "—");

export default function SyncRunsPage() {
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState({});

  const load = async () => {
    const [r, c] = await Promise.all([api.get("/sync-runs?limit=100"), api.get("/companies")]);
    setRuns(r.data);
    setCompanies(Object.fromEntries(c.data.map((x) => [x.id, x.name])));
  };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  // When a run is selected, fetch its items
  useEffect(() => {
    if (!selected) { setItems([]); return; }
    let cancelled = false;
    api.get(`/sync-runs/${selected.id}/items?limit=200`).then(({ data }) => {
      if (!cancelled) setItems(data);
    });
    return () => { cancelled = true; };
  }, [selected]);

  const remove = async (run, e) => {
    e.stopPropagation();
    if (!window.confirm(`Cancellare questa esecuzione? (${run.entity} · ${run.mode} · ${fmt(run.started_at)})\n\nVerranno cancellati anche i suoi items. Le mapping verso FacturaDirecta NON vengono toccate.`)) return;
    try {
      await api.delete(`/sync-runs/${run.id}`);
      toast.success("Esecuzione cancellata");
      if (selected?.id === run.id) setSelected(null);
      load();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="sync-runs-page">
      <Section title="Esecuzioni Sync" subtitle="Tutte le esecuzioni manuali e schedulate">
        <Card className="overflow-hidden">
          <table className="w-full">
            <thead className="bg-surface-hover/50 border-b border-border-default">
              <tr><TH>Inizio</TH><TH>Società</TH><TH>Entità</TH><TH>Modalità</TH><TH>Stato</TH>
              <TH>Letti</TH><TH>OK</TH><TH>Skip</TH><TH>Bloccati</TH><TH>Errori</TH><TH></TH></tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-b border-border-divider table-row-hover">
                  <TD className="font-mono text-xs">{fmt(r.started_at)}</TD>
                  <TD>{companies[r.company_id] || r.company_id.slice(0, 8)}</TD>
                  <TD>{r.entity}</TD>
                  <TD>{r.mode === "dry_run" ? <Badge tone="dryrun">Dry-Run</Badge> : <Badge tone="info">Reale</Badge>}</TD>
                  <TD><StatusBadge status={r.status} /></TD>
                  <TD className="tabular-nums">{r.records_read}</TD>
                  <TD className="tabular-nums">{r.records_created + r.records_updated}</TD>
                  <TD className="tabular-nums">{r.records_skipped}</TD>
                  <TD className="tabular-nums">{r.records_blocked}</TD>
                  <TD className="tabular-nums">{r.records_failed}</TD>
                  <TD>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setSelected(r)} data-testid={`btn-open-run-${r.id}`}>
                        Dettagli <ChevronRight size={14} className="ml-0.5" />
                      </Button>
                      <button
                        onClick={(e) => remove(r, e)}
                        disabled={r.status === "running"}
                        title={r.status === "running" ? "Run in esecuzione, non cancellabile" : "Cancella esecuzione"}
                        className="p-1.5 rounded-md text-ink-muted hover:text-error hover:bg-error/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        data-testid={`btn-delete-run-${r.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </TD>
                </tr>
              ))}
              {runs.length === 0 && <tr><td colSpan={11}><Empty>Nessuna esecuzione.</Empty></td></tr>}
            </tbody>
          </table>
        </Card>
      </Section>

      {selected && (
        <Card className="p-5">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-display text-lg font-medium">Run {selected.id.slice(0, 8)}…</div>
              <div className="text-sm text-ink-muted">{selected.entity} · {selected.mode}</div>
              {selected.summary?.filter?.date_from && (
                <div className="text-xs text-ink mt-1 font-mono">
                  📅 Periodo: {selected.summary.filter.date_from} → {selected.summary.filter.date_to}
                </div>
              )}
              {selected.error_message && <div className="mt-2 text-sm text-error">{selected.error_message}</div>}
            </div>
            <Button variant="ghost" onClick={() => setSelected(null)}>Chiudi</Button>
          </div>
          <div className="mt-4 max-h-[600px] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-surface-hover/50 border-b border-border-default sticky top-0">
                <tr><TH>Entità</TH><TH>Zoho ID</TH><TH>FD ID</TH><TH>Stato</TH><TH>Azione</TH><TH>Messaggio</TH><TH></TH></tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <ItemRow key={it.id} it={it} dryRun={selected?.mode === "dry_run"} />
                ))}
                {items.length === 0 && <tr><td colSpan={7}><Empty>Nessun item.</Empty></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function StatusBadgeInline({ s, dryRun }) {
  // In dry-run all real-write statuses are "skipped" by design (nothing was
  // written). Show a dedicated "dry-run" badge instead of the misleading "skipped".
  if (dryRun) {
    return <Badge tone="dryrun">dry-run</Badge>;
  }
  const map = { created: "success", updated: "info", skipped: "default", failed: "error", blocked: "warning" };
  return <Badge tone={map[s] || "default"}>{s}</Badge>;
}

function ItemRow({ it, dryRun }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-b border-border-divider">
        <TD className="font-mono text-xs">{it.entity}</TD>
        <TD className="font-mono text-xs">{it.zoho_id || "—"}</TD>
        <TD className="font-mono text-xs">{it.fd_id || "—"}</TD>
        <TD><StatusBadgeInline s={it.status} dryRun={dryRun} /></TD>
        <TD className="text-xs">{it.action || "—"}</TD>
        <TD className="text-xs text-ink-muted max-w-md truncate" title={it.message || ""}>{it.message || "—"}</TD>
        <TD>
          {(it.message || it.transformed_preview) && (
            <button
              className="text-xs underline text-ink hover:text-ink/80"
              onClick={() => setOpen((v) => !v)}
              data-testid={`btn-expand-${it.id}`}
            >
              {open ? "Nascondi" : "Apri"}
            </button>
          )}
        </TD>
      </tr>
      {open && (
        <tr className="border-b border-border-divider bg-surface-hover/30">
          <td colSpan={7} className="p-4">
            {it.message && (
              <div className="mb-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1">Messaggio completo</div>
                <pre className="font-mono text-xs whitespace-pre-wrap break-all bg-app rounded p-3 border border-border-default select-all">{it.message}</pre>
              </div>
            )}
            {it.transformed_preview && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1">Payload trasformato</div>
                <pre className="font-mono text-xs whitespace-pre-wrap break-all bg-app rounded p-3 border border-border-default select-all">{JSON.stringify(it.transformed_preview, null, 2)}</pre>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
